
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { getWeekdayWaitAverages } from '../../lib/dbFetcher';
// English weekday slugs, shared across locales (ISODOW 1=Mon..7=Sun).
const SLUGS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
];
const dowFromSlug = (slug) => SLUGS.indexOf(slug) + 1; // 0 -> invalid
export async function getStaticPaths({ locales }) {
    const paths = (locales || ['da']).flatMap((locale) => SLUGS.map((day) => ({ params: { day }, locale })));
    return { paths, fallback: false };
}
async function getStaticProps({ params, locale }) {
    const dow = dowFromSlug(params.day);
    if (dow < 1)
        return { notFound: true };
    let days = [];
    try {
        days = await getWeekdayWaitAverages();
    }
    catch (error) {
        console.error('Error building travel-times page:', error);
        days = Array.from({ length: 7 }, (_, i) => ({
            dow: i + 1,
            avgLow: null,
            busiestHour: null,
            busiestAvg: null
        }));
    }
    const valid = days.filter((d) => d.avgLow != null);
    const weekAvg = valid.length
        ? Math.round((valid.reduce((s, d) => s + d.avgLow, 0) / valid.length) * 10) / 10
        : null;
    return { props: { dow, days, weekAvg, locale: locale || 'da' }, revalidate: 3600 };
}
function formatHour(h) {
    if (h == null)
        return null;
    return `${String(h).padStart(2, '0')}:00`;
}
function TravelTimeDay({ dow, days, weekAvg }) {
    const { t, lang } = useTranslation('travel-times');
    const dayName = t(`day_${dow}`);
    const dayPhrase = lang === 'en' ? `${dayName}s` : `${dayName.toLowerCase()}en`;
    const day = days.find((d) => d.dow === dow) || { avgLow: null };
    const hour = formatHour(day.busiestHour);
    const hasData = day.avgLow != null;
    const answer = hasData
        ? t('answer', { dayPhrase, avg: day.avgLow, hour })
        : t('answer_nodata', { dayPhrase });
    let relation = t('relation_average');
    if (hasData && weekAvg != null) {
        if (day.avgLow > weekAvg + 0.5)
            relation = t('relation_busier');
        else if (day.avgLow < weekAvg - 0.5)
            relation = t('relation_calmer');
    }
    const faqs = [
        { q: t('faq_q1'), a: t('faq_a1') },
        { q: t('faq_q2'), a: t('faq_a2') }
    ];
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a }
        }))
    };
    return (<div className="bg-white p-4 sm:p-6 my-auto rounded max-w-3xl mx-auto text-gray-800">
            <Head>
                <title>{t('meta_title', { dayName, dayPhrase })}</title>
                <meta name="description" content={t('meta_description', { dayName, dayPhrase })}/>
                <meta name="robots" content="all"/>
                <meta property="og:title" content={t('meta_title', { dayName, dayPhrase })}/>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd).replace(/</g, '\\u003c')
        }}/>
            </Head>

            <h1 className="text-2xl sm:text-3xl font-bold">
                {t('page_title', { dayName, dayPhrase })}
            </h1>

            {/* Answer-first, data-bearing statement (GEO-extractable). */}
            <p className="mt-4 text-lg">{answer}</p>
            {hasData && weekAvg != null && (<p className="mt-2 text-gray-700">
                    {t('vs_week', { weekAvg, dayName, relation })}
                </p>)}

            <div className="mt-6">
                <Link href="/" className="text-[var(--secondary)] underline">
                    {t('cta_live')}
                </Link>
            </div>

            <h2 className="text-xl font-semibold mt-8">
                {t('week_table_title')}
            </h2>
            <table className="mt-3 w-full text-left border-collapse">
                <thead>
                    <tr className="border-b">
                        <th className="py-2">{t('col_day')}</th>
                        <th className="py-2">{t('col_avg')}</th>
                        <th className="py-2">{t('col_busiest')}</th>
                    </tr>
                </thead>
                <tbody>
                    {days.map((d) => (<tr key={d.dow} className={`border-b ${d.dow === dow ? 'font-semibold' : ''}`}>
                            <td className="py-2">
                                <Link href={`/travel-times/${SLUGS[d.dow - 1]}`} className="text-[var(--secondary)] underline">
                                    {t(`day_${d.dow}`)}
                                </Link>
                            </td>
                            <td className="py-2">
                                {d.avgLow != null ? d.avgLow : '-'}
                            </td>
                            <td className="py-2">
                                {formatHour(d.busiestHour) || '-'}
                            </td>
                        </tr>))}
                </tbody>
            </table>

            <p className="mt-4 text-xs text-gray-500">{t('source_note')}</p>

            <div className="mt-6 flex flex-col gap-2">
                <Link href="/blog/best-time-to-pass-cph-security" className="text-[var(--secondary)] underline">
                    {t('cta_best_time')}
                </Link>
                <Link href="/blog/how-early-to-arrive-at-cph" className="text-[var(--secondary)] underline">
                    {t('cta_how_early')}
                </Link>
            </div>

            <h2 className="text-xl font-semibold mt-8">FAQ</h2>
            <dl className="mt-3 divide-y divide-gray-200">
                {faqs.map(({ q, a }, i) => (<div key={i} className="py-3">
                        <dt className="font-semibold">{q}</dt>
                        <dd className="mt-1 text-gray-700">{a}</dd>
                    </div>))}
            </dl>
        </div>);
}
export default TravelTimeDay;

    async function __Next_Translate__getStaticProps__1a1723ec75a__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/travel-times/[day]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec75a__ as getStaticProps }
  